// ---------------------------------------------------------------------------
// Period to Granularity Mapper
// ---------------------------------------------------------------------------
// Maps GlobalTimeBar period selections to chart X-axis granularity.
// For custom periods, auto-calculates based on date range span.
// ---------------------------------------------------------------------------

import type { TimePeriod } from '@/components/v11/discovery/types'

export type Granularity = 'daily' | 'weekly' | 'monthly'

/**
 * Determine the chart X-axis granularity based on the selected time period.
 *
 * - Short periods (today, yesterday, 7d, 15d, wtd, mtd) -> daily
 * - Medium periods (3m, qtd) -> weekly
 * - Long periods (6m, 12m, ytd) -> monthly
 * - Custom: auto-calculated from date range (1-31d -> daily, 32-90d -> weekly, 91+ -> monthly)
 */
export function periodToGranularity(
  period: TimePeriod,
  dateFrom?: string | null,
  dateTo?: string | null,
): Granularity {
  // Auto-calculate for custom period based on date span
  if (period === 'custom' && dateFrom && dateTo) {
    const from = new Date(dateFrom)
    const to = new Date(dateTo)
    const diffMs = to.getTime() - from.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1

    if (diffDays <= 31) return 'daily'
    if (diffDays <= 90) return 'weekly'
    return 'monthly'
  }

  switch (period) {
    case 'today':
    case 'yesterday':
    case '7d':
    case '15d':
    case '30d':
    case 'wtd':
    case 'mtd':
      return 'daily'

    case '3m':
    case 'qtd':
      return 'weekly'

    case '6m':
    case '12m':
    case 'ytd':
    case 'custom':
    default:
      return 'monthly'
  }
}
